import { kpiAnalyticsApi } from '../api/axiosAdminInstance';
import { KPI_ANALYTICS_DEFAULT_PASSWORD } from '../config';

const NIA_TOKEN_KEY = 'nia-gateway-token';
const GATEWAY_CLIENT = 'nia-cli';
const APPLICATION_HASH = 'ooJohH1uoM4lahch';

export function getNiaToken(): string | null {
  try {
    const stored = localStorage.getItem(NIA_TOKEN_KEY);
    if (!stored) return null;
    const { access_token, expires_at } = JSON.parse(stored);
    if (Date.now() > expires_at) return null;
    return access_token;
  } catch {
    return null;
  }
}

export async function provisionNiaUser(email: string): Promise<void> {
  // Always re-fetch token to get updated roles/permissions

  const username = email.split('@')[0];
  const password = KPI_ANALYTICS_DEFAULT_PASSWORD;

  // 1. Create user (ignore if already exists)
  let userCreated = false;
  let userId: number | null = null;
  try {
    const createResp = await kpiAnalyticsApi.post('/gateway/api/v1alpha/config/user', {
      username, email, active: true, password, require_otp: false
    });
    userCreated = true;
    userId = createResp.data?.id ?? null;
  } catch {
    // 500 = already exists, that's fine
  }

  // 2. Get token for the user (needed for role assignment and for the app)
  let access_token: string | null = null;
  try {
    const tokenResp = await kpiAnalyticsApi.post(
      '/gateway/api/v1alpha/auth/token',
      `username=${encodeURIComponent(username)}&password=${encodeURIComponent(password)}&grant_type=password`,
      {
        headers: {
          Authorization: `Basic ${btoa(`${GATEWAY_CLIENT}:${APPLICATION_HASH}`)}`,
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      }
    );

    const { expires_in, scope } = tokenResp.data;
    access_token = tokenResp.data.access_token;

    localStorage.setItem(NIA_TOKEN_KEY, JSON.stringify({
      access_token,
      expires_at: Date.now() + (expires_in * 1000) - 60000
    }));

    // Store permissions based on token scope (roles)
    if (scope) {
      const { resolveNiaPermissions, storeNiaPermissions } = await import('../const/niaPermissions');
      storeNiaPermissions(resolveNiaPermissions(scope));
    }

    // Set on window for rlca-module to read
    (window as any).__SWOB_USER_EMAIL__ = email;
    (window as any).__NIA_TOKEN__ = access_token;
  } catch (e) {
    console.error('NIA token acquisition failed:', e);
  }

  // 3. If user was just created, self-assign roles using own token (two-step)
  if (userCreated && access_token && userId) {
    try {
      // Get roles to find Operator and Administrator IDs dynamically
      const rolesResp = await kpiAnalyticsApi.get('/gateway/api/v1alpha/config/role', {
        headers: { Authorization: `Bearer ${access_token}` }
      });
      const roles = rolesResp.data?.items || rolesResp.data || [];
      const operatorRole = roles.find((r: { name: string }) => r.name === 'Operator');
      const adminRole = roles.find((r: { name: string }) => r.name === 'Administrator');

      if (operatorRole) {
        // Step 1: Self-assign Operator (only role allowed for users without roles)
        await kpiAnalyticsApi.put(`/gateway/api/v1alpha/config/user/${userId}`, {
          username, email, active: true, require_otp: false,
          roles: [{ id: operatorRole.id, name: 'Operator', description: operatorRole.description || 'Operator' }]
        }, {
          headers: { Authorization: `Bearer ${access_token}` }
        });

        // Re-fetch token to include Operator role in scope
        const midTokenResp = await kpiAnalyticsApi.post(
          '/gateway/api/v1alpha/auth/token',
          `username=${encodeURIComponent(username)}&password=${encodeURIComponent(password)}&grant_type=password`,
          {
            headers: {
              Authorization: `Basic ${btoa(`${GATEWAY_CLIENT}:${APPLICATION_HASH}`)}`,
              'Content-Type': 'application/x-www-form-urlencoded'
            }
          }
        );
        access_token = midTokenResp.data.access_token;

        // Step 2: Now with Operator role, assign Operator + Administrator
        if (adminRole) {
          await kpiAnalyticsApi.put(`/gateway/api/v1alpha/config/user/${userId}`, {
            username, email, active: true, require_otp: false,
            roles: [
              { id: operatorRole.id, name: 'Operator', description: operatorRole.description || 'Operator' },
              { id: adminRole.id, name: 'Administrator', description: adminRole.description || 'Administrator' }
            ]
          }, {
            headers: { Authorization: `Bearer ${access_token}` }
          });
        }

        // Final token with all roles
        const finalTokenResp = await kpiAnalyticsApi.post(
          '/gateway/api/v1alpha/auth/token',
          `username=${encodeURIComponent(username)}&password=${encodeURIComponent(password)}&grant_type=password`,
          {
            headers: {
              Authorization: `Basic ${btoa(`${GATEWAY_CLIENT}:${APPLICATION_HASH}`)}`,
              'Content-Type': 'application/x-www-form-urlencoded'
            }
          }
        );

        access_token = finalTokenResp.data.access_token;
        const { expires_in, scope } = finalTokenResp.data;

        localStorage.setItem(NIA_TOKEN_KEY, JSON.stringify({
          access_token,
          expires_at: Date.now() + (expires_in * 1000) - 60000
        }));

        if (scope) {
          const { resolveNiaPermissions, storeNiaPermissions } = await import('../const/niaPermissions');
          storeNiaPermissions(resolveNiaPermissions(scope));
        }

        (window as any).__NIA_TOKEN__ = access_token;
      }
    } catch (e) {
      console.warn('Role self-assignment failed:', e);
    }
  }
}
